import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  runTransaction,
} from 'firebase/firestore';
import { AppContext } from './AppContext';
import downloadOnIphone from './assets/downloadOnIphone.png';
import downloadOnGoogle from './assets/downloadOnGoogle.png';

const Showcase = () => {
  const db = getFirestore();

  const [query, setQuery] = useState('');
  const {
    currentUser,
    userId2,
  } = useContext(AppContext);

  const [copyingId, setCopyingId] = useState(null);
  const [termsVisible, setTermsVisible] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [entries, setEntries] = useState([]);
  const [signedUp, setSignedUp] = useState(false);

  const filteredEntries = useMemo(() => {
    const list = Array.isArray(entries) ? entries : [];
    const q = query.trim().toLowerCase();
    if (!q) return list;

    const scoreRow = (e) => {
      const u = (e.username || '').toLowerCase();
      const n = (e.name || '').toLowerCase();
      let score = 0;
      if (u.startsWith(q)) score += 3;
      else if (u.includes(q)) score += 1;
      if (n.startsWith(q)) score += 3;
      else if (n.includes(q)) score += 1;
      return score;
    };

    return list
      .map(e => ({ e, score: scoreRow(e) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score || (a.e.username || '').localeCompare(b.e.username || ''))
      .map(x => x.e);
  }, [entries, query]);

  const normalizedCurrentUser = useMemo(() => {
    if (!currentUser) return { username: '', profileImageUrl: '' };
    const username =
      currentUser.username ||
      currentUser.Username ||
      currentUser.userName ||
      '';
    const profileImageUrl =
      currentUser.profileImageUrl ||
      currentUser.profileImageURL ||
      currentUser.profileImageURI ||
      '';
    return { username, profileImageUrl };
  }, [currentUser]);

  const canEnter = useMemo(() => {
    return Boolean(
      userId2 &&
      normalizedCurrentUser.username &&
      normalizedCurrentUser.profileImageUrl
    );
  }, [userId2, normalizedCurrentUser]);

  const isArtistOrBand = useMemo(() => {
    const t = (currentUser?.accountType || '').trim();
    return t === 'Artist' || t === 'Band';
  }, [currentUser?.accountType]);

  const hasVotedAnywhere = useMemo(() => {
    if (!userId2) return false;
    return entries.some(e => Array.isArray(e.voters) && e.voters.includes(userId2));
  }, [entries, userId2]);

  const docRef = useMemo(
    () => doc(db, 'artistShowcase', 'signups'),
    [db]
  );

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        setEntries([]);
        setSignedUp(false);
        return;
      }

      const data = snap.data() || {};
      const artistsMap = data.artists || {}; // { userId: [ { username, profileImageUrl, ... }, ... ] }

      const list = Object.keys(artistsMap).map(uid => {
        const arr = Array.isArray(artistsMap[uid]) ? artistsMap[uid] : [];
        const latest = arr[arr.length - 1] || {};
        const voters = Array.isArray(latest.voters) ? latest.voters : [];
        const votesCount = typeof latest.votesCount === 'number' ? latest.votesCount : 0;

        return {
          id: uid,
          userId: uid,
          username: latest.username || '',
          name: latest.name || latest.displayName || '',
          profileImageUrl: latest.profileImageUrl || latest.profileImageURL || '',
          createdAt: latest.createdAt || null,
          voters,
          votesCount,
          hasVoted: userId2 ? voters.includes(userId2) : false,
        };
      });

      list.sort((a, b) => {
        const ta = typeof a.createdAt === 'number' ? a.createdAt : a.createdAt?.seconds || 0;
        const tb = typeof b.createdAt === 'number' ? b.createdAt : b.createdAt?.seconds || 0;
        return tb - ta;
      });

      setEntries(list);
      setSignedUp(Boolean(userId2 && artistsMap[userId2] && artistsMap[userId2].length > 0));
    } catch (e) {
      console.error('Showcase fetch error:', e);
      setEntries([]);
      setSignedUp(false);
    } finally {
      setLoading(false);
    }
  }, [docRef, userId2]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const openTerms = () => {
    setTermsVisible(true);
  };

  const agreeAndEnter = async () => {
    try {
      await handleEnterToWin();
    } finally {
      setTermsVisible(false);
    }
  };

  const handleShare = async (artist) => {
    try {
      if (!artist?.username) {
        window.alert('Username is missing for this artist.');
        return;
      }
      const shareLink = `https://spndat.com/showcase/${encodeURIComponent(artist.username)}`;

      setCopyingId(artist.userId);

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
      } else {
        const ta = document.createElement('textarea');
        ta.value = shareLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      window.alert('Link copied!\nShare this link to open the artist’s showcase page.');
    } catch (e) {
      console.error('Copy link error:', e);
      window.alert('Could not copy the link. Please try again.');
    } finally {
      setCopyingId(null);
    }
  };

  const handleEnterToWin = async () => {
    if (!canEnter) {
      window.alert('Please make sure you are signed in and your profile is set up.');
      return;
    }
    try {
      setSubmitting(true);

      const payload = {
        username: normalizedCurrentUser.username,
        profileImageUrl: normalizedCurrentUser.profileImageUrl,
        userId: userId2,
        name: currentUser?.name || currentUser?.displayName || '',
        createdAt: Date.now(),
      };

      await setDoc(
        docRef,
        { artists: { [userId2]: arrayUnion(payload) } },
        { merge: true }
      );

      setEntries(prev => {
        const others = prev.filter(e => e.userId !== userId2);
        const newEntry = {
          id: userId2,
          userId: userId2,
          username: payload.username,
          name: payload.name,
          profileImageUrl: payload.profileImageUrl,
          createdAt: payload.createdAt,
          voters: [],
          votesCount: 0,
          hasVoted: false,
        };
        return [newEntry, ...others];
      });

      setSignedUp(true);
      window.alert('You’re in! Your entry has been submitted to the Artist Showcase.');
    } catch (e) {
      console.error('Showcase submit error:', e);
      window.alert('Could not submit your entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (item) => {
      if (item.username) {
        window.location.href = `/showcase/${encodeURIComponent(item.username)}`;
      }
  };

  const renderEntry = (item) => {
    const voters = Array.isArray(item.voters) ? item.voters : [];
    const votesCount = typeof item.votesCount === 'number' ? item.votesCount : 0;
    const hasVoted = userId2 ? voters.includes(userId2) : false;
    const disableVote = hasVotedAnywhere && !hasVoted;

    const handleOpenArtist = () => {
      if (item.username) {
        window.location.href = `/showcase/${encodeURIComponent(item.username)}`;
      }
    };

    return (
      <div key={item.id || item.userId} style={styles.card}>
        <button onClick={handleOpenArtist} style={styles.cardImgButton}>
          <img
            src={item.profileImageUrl || ''}
            alt={item.username || 'artist'}
            style={styles.avatar}
          />
        </button>

        <div title={item.username || 'unknown'} style={styles.usernameText}>
          {item.username || 'unknown'}
        </div>

        <button
          onClick={() => handleVote(item)}
          disabled={hasVoted || disableVote}
          style={{
            ...styles.voteBtn,
            ...(hasVoted || disableVote ? { opacity: 0.6, cursor: 'not-allowed' } : {})
          }}
        >
          <span style={styles.voteBtnText}>
            {hasVoted ? 'Voted' : 'Vote'}
          </span>
        </button>

        <button
          onClick={() => handleShare(item)}
          disabled={copyingId === item.userId || !item.username}
          style={{
            ...styles.shareBtn,
            ...(copyingId === item.userId ? { opacity: 0.7, cursor: 'wait' } : {})
          }}
        >
          <span style={styles.shareBtnText}>
            {copyingId === item.userId ? 'Sharing…' : 'Share'}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div style={styles.fullScreen}>
      <div style={styles.screen}>
        <div style={styles.headerWrapper}>
          <div style={styles.header}>20k GIVEAWAY SHOWCASE</div>

          <div style={styles.subheader}>
            <strong style={styles.freeWord}>Enter and Vote for Free - One Vote per Account</strong>
            <br /><br />
            Welcome to the Street Flavor Records 20K Giveaway powered by SPNDAT. The top 10 artists with the most votes will advance to the finals and perform live in Nashville, TN. Winners will be chosen based on their performance: 1st place – $10,000, 2nd – $5,000,
            <br />
            3rd – $3,000, 4th – $2,000.
            <br /><br />
            Download the SPNDAT app to enter or vote. For more details, contact support@spndat.com.
          </div>

<div style={styles.storeButtonsWrapper}>
  <div
    onClick={() => window.open('https://apps.apple.com/us/app/spndat/id6503178936', '_blank')}
    style={{
      cursor: 'pointer',
      marginTop: '-57px',
    }}
  >
    <img
      src={downloadOnIphone}
      alt="Download on iOS"
      style={{
        objectFit: 'contain',
        width: '200px',
        height: '200px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        display: 'block',
      }}
    />
  </div>

  <div
    onClick={() => window.open('https://play.google.com/store/apps/details?id=com.spndat.spndat', '_blank')}
    style={{
      cursor: 'pointer',
      marginTop: '-54px',
    }}
  >
    <img
      src={downloadOnGoogle}
      alt="Download on Google"
      style={{
        objectFit: 'contain',
        width: '220px',
        height: '100px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        display: 'block',
      }}
    />
  </div>

</div>



          {isArtistOrBand && (
            <button
              onClick={signedUp ? undefined : openTerms}
              disabled={!canEnter || submitting || signedUp}
              style={{
                ...styles.enterBtn,
                ...((!canEnter || submitting) && !signedUp ? styles.enterBtnDisabled : {}),
                ...(signedUp ? styles.enterBtnSigned : {}),
              }}
            >
              {submitting ? (
                <span>Submitting…</span>
              ) : (
                <span style={styles.enterBtnText}>
                  {signedUp ? "YOU'RE SIGNED UP" : 'ENTER TO WIN'}
                </span>
              )}
            </button>
          )}

          <div style={styles.sectionTitle}>Artists Entered</div>

          <div style={styles.searchRow}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by username or name"
              style={styles.searchInput}
              autoCapitalize="none"
              autoCorrect="off"
            />
          </div>

          {loading && (
            <div style={styles.loading}>Loading…</div>
          )}
        </div>

        {!loading && filteredEntries.length === 0 ? (
          <div style={styles.emptyText}>No artists have entered yet. Be the first!</div>
        ) : null}

        {/* Two-per-row grid */}
        <div style={styles.grid}>
          {!loading && filteredEntries.map(renderEntry)}
        </div>
      </div>

      {/* Terms Modal */}
      {termsVisible && (
        <div style={styles.modalBackdrop} onClick={() => setTermsVisible(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>Before You Enter</div>

            <div style={styles.modalScroll}>
              <div style={styles.modalText}>
                Votes will be tallied on November 25th and finalists will be contacted by a Street Flavor representative.
                The top four artists will advance to the finals and perform live on December 4th in Nashville, TN at the Vinyl Lounge.
                Final winners will be chosen based on their performance that night. If a finalist is unable to attend, their spot goes to the next contestant with the most votes. Street Flavor Records and SPNDAT are not responsible for any expenses incurred by the finalists. Any contestants found to be using illegitimate voting methods will be disqualified immediately.
              </div>
            </div>

            <div style={styles.modalButtonsRow}>
              <button
                style={{ ...styles.modalBtn, ...styles.modalSecondaryBtn }}
                onClick={() => setTermsVisible(false)}
              >
                <span style={styles.modalSecondaryText}>Cancel</span>
              </button>

              <button
                style={{ ...styles.modalBtn, ...styles.modalPrimaryBtn }}
                onClick={agreeAndEnter}
                disabled={submitting}
              >
                <span style={styles.modalPrimaryText}>
                  {submitting ? 'Submitting…' : 'Agree and Enter to Win'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Styles (px only; scrollable page and exactly two cards per row) **/
const styles = {
  fullScreen: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflowY: 'auto',         // make the whole view scrollable
    backgroundColor: '#0e061e',
  },
  screen: {
    position: 'relative',
    zIndex: 2,
    paddingTop: '16px',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '220px',
    maxWidth: '1200px',
    margin: '0 auto',

  },
  headerWrapper: {
    textAlign: 'center',
  },
  storeButtonsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '4px',
    marginBottom: '4px',
  },


  header: {
    color: 'white',
    fontSize: '28px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  subheader: {
    color: '#cfd3d6',
    fontSize: '16px',
    textAlign: 'center',
    marginLeft: '8px',
    marginRight: '8px',
    marginBottom: '20px',
    lineHeight: '22px',
  },
  freeWord: {
    color: 'white',
    fontWeight: 'bold',
  },
  enterBtn: {
    display: 'inline-block',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '28px',
    paddingRight: '28px',
    borderRadius: '12px',
    backgroundColor: '#62fbfa',
    marginBottom: '18px',
    border: 'none',
    cursor: 'pointer',
  },
  enterBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  enterBtnSigned: {
    backgroundColor: '#62fbfa',
  },
  enterBtnText: {
    fontSize: '18px',
    color: '#000',
    fontWeight: 'bold',
    fontFamily: 'brhendrix, sans-serif',
    marginTop: '4px',
  },
  sectionTitle: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: '20px',
    marginBottom: '10px',
    marginTop: '6px',
    textAlign: 'left',
  },
  searchRow: {
    marginBottom: '12px',
    marginTop: '6px',
  },
  searchInput: {
    width: '100%',
    borderRadius: '10px',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    color: 'white',
    fontSize: '16px',
    border: '1px solid rgba(255,255,255,0.1)',
    outline: 'none',
  },
  loading: {
    color: '#62fbfa',
    marginTop: '16px',
  },
  emptyText: {
    color: '#b8b8b8',
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '16px',
  },

  /* EXACTLY two columns */
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr', // two per row
    gap: '18px',
    alignItems: 'stretch',
  },
  card: {
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: '12px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '12px',
    paddingRight: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  cardImgButton: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  avatar: {
    width: '200px',
    height: '200px',
    borderRadius: '100px',
    objectFit: 'cover',
    display: 'block',
  },
  usernameText: {
    marginTop: '10px',
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold',
    maxWidth: '240px',
    textAlign: 'center',
    fontFamily: 'brhendrix, sans-serif',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  voteBtn: {
    marginTop: '10px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '28px',
    paddingRight: '28px',
    borderRadius: '10px',
    backgroundColor: '#62fbfa',
    border: 'none',
    cursor: 'pointer',
  },
  voteBtnText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: '16px',
    fontFamily: 'brhendrix, sans-serif',
  },
  votesCountLabel: {
    marginTop: '8px',
    color: '#cfd3d6',
    fontSize: '14px',
  },
  shareBtn: {
    marginTop: '10px',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '24px',
    paddingRight: '24px',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    border: '1px solid #62fbfa',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  shareBtnText: {
    color: '#62fbfa',
    fontWeight: 'bold',
    fontSize: '16px',
    fontFamily: 'brhendrix, sans-serif',
    textAlign: 'center',
  },

  /* Modal */
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
    zIndex: 9999,
  },
  modalCard: {
    width: '94vw',
    maxWidth: '720px',
    height: '60vh',
    maxHeight: '600px',
    backgroundColor: '#0e061e',
    borderRadius: '16px',
    paddingTop: '24px',
    paddingBottom: '24px',
    paddingLeft: '24px',
    paddingRight: '24px',
    border: '1px solid rgba(98,251,250,0.25)',
    display: 'flex',
    flexDirection: 'column',
  },
  modalTitle: {
    color: 'white',
    fontSize: '24px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '16px',
  },
  modalScroll: {
    flex: '1 1 auto',
    overflowY: 'auto',
    paddingRight: '6px',
  },
  modalText: {
    color: '#cfd3d6',
    fontSize: '16px',
    lineHeight: '22px',
  },
  modalButtonsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '12px',
    marginTop: '16px',
  },
  modalBtn: {
    flex: '1',
    paddingTop: '14px',
    paddingBottom: '14px',
    borderRadius: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'inline-flex',
    border: 'none',
    cursor: 'pointer',
  },
  modalSecondaryBtn: {
    flex: '0.6',
    backgroundColor: 'transparent',
    border: '1px solid #62fbfa',
  },
  modalPrimaryBtn: {
    flex: '1.08',
    backgroundColor: '#62fbfa',
  },
  modalSecondaryText: {
    color: '#62fbfa',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  modalPrimaryText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: '16px',
  },
};

export default Showcase;
